import { FC } from 'react'
import cx from 'classnames'
import { useTranslation } from 'next-i18next'

import { ChevronIcon } from 'icons'

export interface IPaginationProps {
  className?: string
  currentPage: number
  totalPages: number
  setCurrentPage: (page: number) => void
}

const getPages = (currentPage: number, totalPages: number) => {
  const maxPages = 7

  // few enough to show all
  if (totalPages <= maxPages) {
    return Array(totalPages)
      .fill(null)
      .map((_, i) => i + 1)
  }

  // selected page is within 3 of beginning
  if (currentPage - 3 < 1) {
    return Array(maxPages)
      .fill(null)
      .map((_, i) => i + 1)
  }

  // selected page is within 3 of end
  if (currentPage + 3 > totalPages) {
    return Array(maxPages)
      .fill(null)
      .map((_, i) => totalPages - (maxPages - (i + 1)))
  }

  // otherwise grab 3 before and 3 after
  return Array(maxPages)
    .fill(null)
    .map((_, i) => currentPage - (3 - i))
}

const Pagination: FC<IPaginationProps> = ({
  className,
  currentPage,
  totalPages,
  setCurrentPage = () => null,
}) => {
  const { t } = useTranslation('common')

  if (totalPages === 1) {
    return null
  }

  const pages = getPages(currentPage, totalPages)
  const previousEnabled = currentPage > 1
  const nextEnabled = currentPage < totalPages

  return (
    <nav aria-label="results pagination" className={className}>
      <ul className="flex items-center space-x-8">
        <li>
          <button
            className="block"
            disabled={!previousEnabled}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            <ChevronIcon
              aria-hidden="true"
              className={cx('rotate-90 rect-24', {
                'stroke-grey-6': !previousEnabled,
                'stroke-teal-3': previousEnabled,
              })}
            />
            <span className="sr-only">{t('ui.previous-page')}</span>
          </button>
        </li>
        {pages.map((num) => {
          const isSelected = num === currentPage
          return (
            <li key={num}>
              <button
                className={cx(
                  'rounded-sm border border-teal-3 py-4 px-8 text-12',
                  {
                    'bg-teal-3 text-white': isSelected,
                    'text-teal-3 hover:bg-teal-2': !isSelected,
                  }
                )}
                onClick={() => setCurrentPage(num)}
              >
                <span className="sr-only">{t('ui.page')}</span> {num}
              </button>
            </li>
          )
        })}
        <li>
          <button
            className="block"
            disabled={!nextEnabled}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            <ChevronIcon
              aria-hidden="true"
              className={cx('-rotate-90 rect-24', {
                'stroke-grey-6': !nextEnabled,
                'stroke-teal-3': nextEnabled,
              })}
            />
            <span className="sr-only">{t('ui.next-page')}</span>
          </button>
        </li>
      </ul>
    </nav>
  )
}

export default Pagination
